/**
 * Deciding where the account service lives.
 *
 * Separated from the client, and pure, because the interesting behaviour is the *absence*
 * of a value: the viewer is a client-side application that happens to offer accounts, so
 * "no API configured" is a supported way to build it and not an error. Getting that wrong
 * in either direction is bad — a thrown error would take the whole viewer down over a
 * feature nobody asked for, and a silently wrong default would produce a sign-in button
 * that fails every time it is pressed.
 */

import { logger } from '../log.js';

const log = logger('accounts');

/**
 * Where the dev server's backend is expected, so `npm run dev` needs no `.env` file to
 * exercise the sign-in flow. Only ever used in a development build.
 */
export const DEVELOPMENT_API_BASE_URL = 'https://localhost:8443';

/**
 * @param configured `VITE_API_BASE_URL`, as given at build time.
 * @param isDevelopment Whether this is a development build.
 * @returns The origin to call, or `undefined` to run with no accounts at all.
 */
export function resolveApiBaseUrl(
  configured: string | undefined,
  isDevelopment: boolean,
): string | undefined {
  const trimmed = configured?.trim() ?? '';

  if (trimmed === '') {
    return isDevelopment ? DEVELOPMENT_API_BASE_URL : undefined;
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    log.error(
      `VITE_API_BASE_URL is not a valid URL (${trimmed}); accounts are disabled.`,
    );
    return undefined;
  }

  // The API refuses plaintext anyway, so a http:// origin here would produce a sign-in
  // button that fails on every press. Localhost is exempt: browsers treat it as a secure
  // context, which is what makes the tethered-headset development loop work.
  if (parsed.protocol !== 'https:' && !isLoopback(parsed.hostname)) {
    log.error(`VITE_API_BASE_URL must be https (${trimmed}); accounts are disabled.`);
    return undefined;
  }

  // Normalised to the origin. A path would be dropped when resolving `/api/...` against
  // it in any case, so keeping one would only be a way to be misled about what is called.
  return parsed.origin;
}

function isLoopback(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}
